// Bilingual (EN / 中文) strings for the admin + organizer portals.
//
// The public marketing site uses src/i18n/translations.js. This file covers the
// authenticated dashboards which were previously hardcoded Chinese. Access via
// the usePortalT() hook (src/i18n/usePortalT.js), e.g. t('common.save') or
// tEnum('configStatus', 'published').

export const portal = {
  en: {
    lang: { en: 'EN', zh: '中文' },
    common: {
      loading: 'Loading…',
      save: 'Save',
      saving: 'Saving…',
      saveDraft: 'Save draft',
      cancel: 'Cancel',
      delete: 'Delete',
      remove: 'Remove',
      edit: 'Edit',
      detail: 'Details',
      refresh: 'Refresh',
      back: 'Back',
      backToList: '← Back to list',
      search: 'Search',
      query: 'Query',
      status: 'Status',
      all: 'All',
      logout: 'Log out',
      unlimited: 'Unlimited',
      none: 'None',
      optional: 'optional',
      processing: 'Processing…',
      dash: '—',
      create: 'Create',
      creating: 'Creating…',
      loadFailed: 'Load failed',
      saveFailed: 'Save failed',
      createFailed: 'Create failed',
      deleteFailed: 'Delete failed',
      updateFailed: 'Update failed',
      actionFailed: 'Action failed',
    },
    adminNav: {
      brand: 'OnMyWay Admin',
      pvp: 'Timed events',
      activities: 'Activity rewards',
      newActivity: 'New activity',
      reports: 'Reports',
      appeals: 'Appeals',
      rewards: 'Points store',
    },
    orgNav: {
      brand: 'OnMyWay · Organizer',
      myActivities: 'My activities',
      create: 'Create activity',
      routeRewards: 'Route rewards',
      vouchers: 'Voucher redemption',
    },
    adminLogin: {
      title: 'OnMyWay Admin',
      subtitle: 'Sign in with an admin account to enter the operations dashboard.',
      userLabel: 'Username / Email',
      passwordLabel: 'Password',
      signIn: 'Sign in',
      signingIn: 'Signing in…',
      notAdmin: 'This account is not an admin account. Activity organizers / merchants please go to the activity portal.',
      goOrganizer: 'Go to the activity portal',
      organizerPrompt: 'Activity organizer / merchant?',
      organizerLink: 'Go to the activity portal',
      failed: 'Sign in failed',
    },
    orgLogin: {
      title: 'Activity Organizer',
      titleFull: 'Organizer sign in',
      loadingTitle: 'Activity Organizer',
      subtitle: 'Sign in with your OnMyWay app account to create and manage activities, points, badges, lotteries and reward redemption.',
      userLabel: 'Username / Email / Phone',
      passwordLabel: 'Password',
      signIn: 'Sign in',
      signingIn: 'Signing in…',
      adminPrompt: 'OnMyWay operations admin?',
      adminLink: 'Go to the operations dashboard',
      failed: 'Sign in failed',
    },
    activities: {
      listTitle: 'My activities',
      listTitleAdmin: 'Activity rewards',
      listSubtitle: 'Create activity reward configs: fill the template or describe in one sentence, let AI compile executable rules, then publish after review.',
      createTemplate: '+ Create from template',
      createNL: 'Create from a sentence',
      empty: 'No activities yet. Click a button above to start.',
      colName: 'Activity',
      colStatus: 'Status',
      colType: 'Type',
      colStart: 'Start',
      colEnd: 'End',
      colUpdated: 'Updated',
      unnamed: '(unnamed)',
      typeNL: 'Natural language',
      typeTemplate: 'Template',
      // Editor
      editorUnnamed: '(unnamed activity)',
      notFound: 'Activity not found',
      subtitleNL: 'Describe the activity in one sentence and AI compiles it into executable rules.',
      subtitleTpl: 'Fill in the 7 modules; AI compiles them into backend-executable activity rules. Do not change the field structure.',
      draftSaved: 'Draft saved',
      draftRestored: 'Restored your unsaved changes from this device.',
      draftDiscard: 'Discard & load saved',
      compileOk: 'AI has generated the activity rules, please confirm below',
      published: 'Published!',
      createdNeedRouteSetup: 'Created! Some routes have physical nodes — open your draft box in the app to bury them on-site and finish setup, then those routes go live.',
      goRouteSetup: 'Send to app for route setup',
      routesPublished: 'Created & published! All routes were finalized on the web.',
      // Route co-creation workbench (AI console)
      secRouteWorkbench: '01a · Route co-creation',
      routeWorkbenchHint: 'Fill in each node yourself, or — for members — let AI draft it. Virtual nodes (GPS only) are placed entirely on the web; nodes needing a physical bury are finished on-site in the app.',
      rwManualNote: 'AI draft is members-only. Anyone can fill the nodes below by hand.',
      rwNodes: 'nodes',
      rwNode: 'Node',
      rwRouteTitle: 'Route title',
      rwRouteSynopsis: 'Route synopsis',
      rwTheme: 'Theme',
      rwThemePh: 'e.g. urban reporter mystery, library architecture, public art',
      rwStyle: 'Route style',
      rwDeadline: 'Deadline (limited-time)',
      rwDeadlineHint: 'After this time the route can no longer be played in the app — players can only view its details (comments, stats).',
      rwInspiration: 'Your inspiration',
      rwInspirationPh: 'Describe the atmosphere, buildings, story threads, or any spark of inspiration',
      rwNodeTypes: 'Preferred node types',
      rwNodeTypesHint: 'Pick the puzzle types you want in this route — AI biases riddles toward them (at least one).',
      rwAiGenerate: 'AI generate draft',
      rwAiGenerating: 'Generating…',
      rwNodeNoGps: 'no GPS — pin it on the map above',
      rwNodePhysical: 'Needs physical bury',
      rwNodeQuestion: 'Riddle / question',
      rwNodeAnswer: 'Answer',
      rwNodeHint: 'Hint',
      rwNodePhysicalHint: 'This node needs a physical object buried on-site — finish it in the app.',
      rwNodeVirtualHint: 'Virtual node — placed on the web once it has GPS + an answer.',
      rwNodeMedia: 'Node image / video',
      rwNodeMediaHint: ' — shown as the transition background when players reach this stage',
      rwNodeMediaAdd: 'Upload media',
      rwAiRefine: 'Refine with AI',
      rwAiRefinePh: 'e.g. "Make node 2 harder" or "adjust the theme"',
      rwAiRefineSend: 'Send',
      rwRouteNeedsApp: 'Has physical nodes → finish in the app after Create',
      rwRouteWebComplete: 'All virtual → finalized on the web on Create',
      rwVirtualPlaced: 'Virtual placed',
      rwPhysicalCount: 'Physical',
      aiDraftDone: 'AI draft applied to the route.',
      aiDraftFailed: 'AI generation failed.',
      aiPremiumOnly: 'AI draft is a members-only feature — you can still fill each node manually below.',
      routeNeedsWaypoints: 'Draw at least one waypoint on the map first.',
      archived: 'Unpublished',
      hasErrors: 'The rules contain errors, please fix them first',
      publishFailed: 'Publish failed',
      archiveFailed: 'Unpublish failed',
      compileFailed: 'Compile failed',
      simulateFailed: 'Simulation failed',
      confirmDelete: 'Delete this activity config?',
      drawDone: 'Lottery drawn, {n} winner(s)',
      drawFailed: 'Draw failed',
      aiRules: '🤖 AI-generated activity rules',
      pointsGroup: 'Points',
      progressionGroup: 'Progression',
      rewardsGroup: 'Rewards',
      lotteryGroup: 'Lottery',
      simulate: 'Simulate',
      routesDone: 'Routes completed',
      includeJoin: 'Include join activity',
      runSim: 'Run simulation',
      simPoints: 'Points',
      simRoutesCompleted: 'Routes completed',
      simTickets: 'Lottery tickets',
      simBadges: 'Badges unlocked',
      simRedeemable: 'Redeemable',
      readyToPublish: '✅ Ready to publish',
      simSec: '🧪 Reward & point simulator',
      simSecHint: 'For your expected turnout, enter how many participants will complete each action. Totals update live so you can balance point payout against reward costs, prizes and sponsor budget.',
      simTargetLabel: 'Expected participants',
      simNoRules: 'Add point rules above to simulate the reward economy.',
      simActionCol: 'Action',
      simPointsEachCol: 'Points each',
      simExpectedCol: '# participants',
      simSubtotalCol: 'Points minted',
      simTotalMinted: 'Total points minted',
      simAvgPerParticipant: 'Avg per participant',
      simEconomy: 'Economy',
      simTotalPoints: 'Est. total points distributed',
      simSetTarget: 'Set target participants for a per-person estimate.',
      simRewardCost: 'Total reward cost (pts):',
      simBudget: 'Sponsor budget:',
      simAffordable: 'A typical participant can redeem: {list}',
      simNoneAffordable: 'A typical participant cannot yet afford any reward — consider lowering costs or raising point rewards.',
      qrSec: '📱 Participation QR codes',
      qrSecHint: 'Print these and display them at the venue. Participants scan with the in-app scanner or the iOS camera to join the activity or check in to a timed mini-activity and earn points.',
      qrPublishFirst: 'Codes only work once the activity is published.',
      qrJoinTitle: 'Join activity',
      qrDownload: 'Download PNG',
      optimizeTitle: '🤖 AI point-rule co-pilot',
      optimizeHint: 'Chat with an AI economist. It reads your sponsors, rewards, target participants and lottery, then proposes a balanced point-rule set you can apply in one click.',
      optimizeThinking: 'Thinking…',
      optimizeSuggested: 'Suggested point rules',
      optimizeApply: 'Apply these rules',
      optimizeApplied: 'Applied the AI-suggested point rules.',
      optimizeSend: 'Send',
      optimizePlaceholder: 'e.g. Keep total payout under budget for 500 people, and make the grand prize feel achievable…',
      optimizeAutoAsk: 'Please review my current point rules and suggest an optimized set.',
      optimizeFailed: 'Optimization failed. Please try again.',
      confirmPublish: '✓ Confirm & publish',
      editRules: 'Edit rules',
      publishedSuccess: '✅ Activity created and live in the app',
      editPublished: 'Edit activity',
      updatePublished: 'Update live activity',
      updatePublishedDone: 'Activity updated. Changes are live in the app.',
      updatePublishedFailed: 'Could not update the live activity.',
      unpublish: 'Unpublish',
      ops: 'Operations data',
      opsHint: 'Live participation, route performance, mini-activity check-ins, reward economy and 14-day heat.',
      registrations: 'Registrations (RSVP)',
      participants: 'Participants',
      active7d: 'Active (7 days)',
      engagementRate: 'Point-earning rate',
      pointsAwarded: 'Total points awarded',
      routeCompletions: 'Route completions',
      routeCompletionRate: 'Route completion rate',
      totalDistanceEstimated: 'Total journey (est.)',
      avgDistanceEstimated: 'Avg walk / route participant (est.)',
      avgRouteDuration: 'Avg route duration',
      redemptions: 'Reward redemptions',
      tickets: 'Lottery tickets',
      winners: 'Winners',
      distanceMethodHint: 'Distance is estimated from configured route length × completed routes. Duration uses measured route start/completion time; mini-activity duration uses its scheduled window because GPS distance and checkout are not currently collected.',
      routeAnalytics: 'Route exploration performance',
      sessionAnalytics: 'Timed mini-activity performance',
      actionAnalytics: 'Point-action engagement',
      heatTrend: 'New participants · last 14 days',
      analyticsName: 'Name',
      analyticsParticipants: 'Participants',
      analyticsCompletions: 'Completions',
      analyticsCompletionRate: 'Completion rate',
      analyticsAvgDuration: 'Avg duration',
      analyticsRouteKm: 'Route km',
      analyticsTotalKm: 'Total journey km (est.)',
      analyticsSchedule: 'Schedule',
      analyticsCheckins: 'Check-ins',
      analyticsScheduledDuration: 'Scheduled duration',
      analyticsParticipationTime: 'Total participation time (est.)',
      analyticsNoData: 'No participation data yet.',
      people: 'people',
      times: 'times',
      draw: 'Draw lottery',
      nlHeading: 'Describe your activity in one sentence',
      nlHint: 'e.g. “Create a 2-week Sydney hiking challenge; finishing 5 routes earns a Patagonia cap; every 500 points earns one lottery entry, up to 5 times.”',
      activityDesc: 'Activity description',
      genRules: 'Create',
      // Section 01
      sec01: '01 · Activity info',
      fName: 'Activity name*',
      fType: 'Activity type',
      geoLabel: 'Location / Route',
      geoHint: ' · Add one or more places, or connect points on the map into one / several routes (combinable)',
      fDesc: 'Activity description*',
      fStart: 'Start date*',
      fEnd: 'End date*',
      fRedeemDeadline: 'Redemption deadline',
      fTarget: 'Target participants',
      mediaLabel: 'Photos & videos',
      mediaHint: ' · Shown in the app event carousel (first item = cover). Drag tiles to reorder.',
      uploadMedia: 'Add photo / video',
      mediaCover: 'Cover',
      mediaImage: 'Photo',
      mediaVideo: 'Video',
      uploadMediaBadType: 'Unsupported file type (use PNG / JPG / WebP / MP4 / MOV)',
      publishNeedLocation: 'Add at least one place or route on the map before publishing',
      geoPoint: 'Point',
      geoRoute: 'Route',
      // Section 01b · Timed sub-activities
      secSessions: '01b · Timed sub-activities',
      secSessionsHint: 'Optional. Break the big activity into timed sessions on specific dates (e.g. Day 1 morning hike, Day 2 workshop). Each can be pinned to one of the places/routes you drew above.',
      fSessionName: 'Session name*',
      fSessionDate: 'Date',
      fSessionStart: 'Start time',
      fSessionEnd: 'End time',
      fSessionLocation: 'Place (optional)',
      fSessionRoute: 'Route (optional)',
      fSessionDesc: 'Notes',
      sessionNoPlace: '— No place —',
      sessionNoRoute: '— No route —',
      addSession: '+ Add sub-activity',
      removeSession: 'Remove sub-activity',
      // Section 02
      sec02: '02 · Sponsors & merchants',
      sec02Hint: 'Search a merchant by name to auto-link their OnMyWay account. Rewards assigned to a sponsor (in section 06/07) appear under it automatically.',
      fMerchantName: 'Merchant name',
      fMerchantUserId: 'OnMyWay User ID',
      fSponsorType: 'Type',
      fContribution: 'Contribution',
      fBudget: 'Budget (AUD)',
      fRewardRefId: 'Reward/Coupon ID',
      removeSponsor: 'Remove sponsor',
      addSponsor: '+ Add sponsor',
      merchantSearchPh: 'Type to search merchants…',
      merchantLinked: 'Linked',
      merchantUnlink: 'Unlink',
      merchantUnlinked: 'Not linked — pick a merchant from the list to link the account.',
      merchantNoResults: 'No matching merchants',
      sponsorRewardsTitle: 'Rewards from this sponsor',
      sponsorRewardsEmpty: 'No rewards assigned yet. Create a reward in section 06/07 and choose this sponsor.',
      sponsorRewardsNeedLink: 'Link a merchant account first to assign rewards.',
      untitledReward: '(untitled reward)',
      rewardIssued: 'Issued',
      rewardPendingIssue: 'Pending issue',
      fRewardSponsor: 'Sponsor',
      rewardSponsorNone: 'Unassigned',
      // Sponsor issuance confirmation
      confirmIssue: 'Confirm issuance',
      issueResend: 'Re-send request',
      issueSent: 'Issuance request sent — the sponsor will confirm in their app.',
      issueResent: 'Request re-sent to the sponsor.',
      issuePending: 'Awaiting sponsor',
      issueConfirmed: 'Confirmed',
      issueRejected: 'Declined',
      issueNotSent: 'Not requested',
      issueNoSponsor: 'Assign a linked sponsor to this reward first.',
      issueRewardNotFound: 'Save the reward before requesting issuance.',
      issueRefresh: 'Refresh',
      // Section 03
      sec03: '03 · Point-earning rules',
      sec03Hint: 'Each Action is a way for players to earn points (scan to join, complete route, invite friends…). The system verifies completion automatically based on the action.',
      fAction: 'Action',
      fPoints: 'Points',
      fDailyLimit: 'Daily cap',
      fTotalLimit: 'Total cap',
      fVerification: 'Verification',
      verificationFixedHint: 'Set automatically by the action: complete route = route clear (GPS); join / timed mini-activity = scan QR; invite / review = system.',
      fSessionBind: 'Timed mini-activity',
      sessionBindAny: 'Any timed mini-activity',
      fRuleDesc: 'Description',
      unlimitedPh: 'Unlimited',
      addPointRule: '+ Add point rule',
      // Section 04
      sec04: '04 · Route / badge progression',
      fBadgeName: 'Badge name',
      fBadgeEmoji: 'Icon emoji',
      fBadgeImage: 'Badge image / SVG',
      uploadImage: 'Upload image',
      uploading: 'Uploading…',
      removeImage: 'Remove image',
      uploadFailed: 'Upload failed',
      uploadTooLarge: 'File too large',
      uploadBadType: 'Unsupported file type (use PNG / JPG / WebP / SVG)',
      storageOff: 'Uploads are not enabled on the server',
      fBadgeType: 'Type',
      fLevel: 'Level',
      fRequirement: 'Requirement',
      fCountThreshold: 'Count / threshold',
      fRouteId: 'Route',
      fLocationId: 'Location',
      selectRoute: 'Select route…',
      selectLocation: 'Select location…',
      fBadgePoints: 'Bonus points',
      removeBadge: 'Remove badge',
      addBadge: '+ Add badge',
      // Section 05
      sec05: '05 · Lottery config',
      enableLottery: 'Enable lottery',
      fPointsPerTicket: 'Every N points = 1 ticket',
      fEntryThreshold: 'Or entry point threshold',
      fMaxEntries: 'Max tickets per user',
      fLotteryDate: 'Draw date',
      fWinnerCount: 'Prize quantity',
      fPrize: 'Prize',
      fSponsorUserId: 'Sponsor User ID',
      lotteryEntryHint: 'How players earn tickets (shared across all prize pools).',
      ticketTiersTitle: 'Points → tickets exchange tiers',
      ticketTiersHint: 'Let players exchange their points for tickets at custom rates (e.g. 100 pts → 1 ticket, 500 pts → 6 tickets). When any tier is set, players spend points to get tickets instead of auto-earning them, and the single "N points = 1 ticket" field above is ignored.',
      fTierPoints: 'Points to spend',
      fTierTickets: 'Tickets received',
      addTier: '+ Add exchange tier',
      removeTier: 'Remove tier',
      ticketDropsTitle: 'Random ticket drops in routes',
      ticketDropsHint: 'Scatter lottery tickets randomly along route explorations. Players who reach a node have a chance to pick one up.',
      enableTicketDrops: 'Enable random drops',
      fDropTotal: 'Total tickets to drop',
      fDropMaxPerUser: 'Max per player',
      fDropRate: 'Drop chance per node (0–1)',
      fDropStart: 'Drop window start',
      fDropEnd: 'Drop window end',
      lotteryPoolsTitle: 'Prize pools',
      lotteryPoolsHint: 'Add one pool per prize and set its quantity. Tickets are shared; the draw is ticket-weighted and guarantees every prize is given out.',
      fPoolName: 'Pool name',
      poolNamePh: 'e.g. Grand prize',
      addPool: '+ Add prize pool',
      removePool: 'Remove pool',
      // Section 06/07
      sec0607: '06 / 07 · Reward redemption & inventory',
      sec0607Hint: 'Three redemption modes: points only / points + badge / badge only. Set inventory and per-user cap.',
      fRewardName: 'Reward name',
      fRedemptionMode: 'Redemption mode',
      fCostPoints: 'Points required',
      fRequiredBadge: 'Required badge',
      selectBadge: 'Select badge…',
      fRewardKind: 'Type',
      fValue: 'Value (AUD)',
      fInventoryPolicy: 'Inventory policy',
      fInventory: 'Inventory qty',
      fPerUserLimit: 'Per-user cap',
      fTotalBudget: 'Total budget (AUD)',
      fExpiryType: 'Expiry type',
      fDays: 'Days',
      fDate: 'Date',
      removeReward: 'Remove reward',
      addReward: '+ Add reward',
    },
    campaigns: {
      dashTitle: 'Timed Events Dashboard',
      dashSubtitle: 'View all official PvP timed events and participation data, and create or edit events.',
      totalEvents: 'Total events',
      active: 'Active',
      totalParticipants: 'Total participants',
      totalMatches: 'Total matches',
      newEvent: '+ New event',
      emptyList: 'No events yet. Click “New event” to create your first timed event.',
      colEvent: 'Event',
      colStatus: 'Status',
      colParticipants: 'Participants',
      colMatches: 'Matches',
      colTime: 'Time',
      createTitle: 'New timed event',
      createSubtitle: 'Create an official PvP hide-and-seek scoring event. The slug cannot be changed after creation.',
      createBtn: 'Create event',
      editTitle: 'Edit timed event',
      backToDetail: 'Back to details',
      endEvent: 'End event',
      confirmEndExport: 'After ending, points stop accruing; you can export the leaderboard for offline prizes. End it?',
      confirmEnd: 'After ending, points stop accruing. End this event?',
      endFailed: 'Failed to end',
      // form
      titleZh: 'Chinese title',
      titleEn: 'English title',
      descZh: 'Chinese description',
      descEn: 'English description',
      statusDraft: 'Draft',
      statusActive: 'Active',
      statusEnded: 'Ended',
      startsAt: 'Start time (ISO)',
      endsAt: 'End time (ISO)',
      setOpenEnded: 'Set open-ended (2099)',
      formRequired: 'Please fill in the slug and Chinese/English titles',
      // detail
      exportCsv: 'Export leaderboard CSV',
      info: 'Event info',
      start: 'Start:',
      end: 'End:',
      lastMatch: 'Last match:',
      noZhDesc: '(no Chinese description)',
      leaderboard: 'Leaderboard',
      noPoints: 'No score data yet',
      colUser: 'User',
      colPoints: 'Points',
      colPlayed: 'Played',
      colWon: 'Won',
      lobbyRooms: 'Lobby rooms',
      noRooms: 'No waiting rooms right now',
      colRoomId: 'Room ID',
      colRoomName: 'Name',
      colMode: 'Mode',
      colMembers: 'Members',
    },
    reports: {
      title: 'Reports Dashboard',
      subtitle: 'Aggregates all user-submitted reports, applies penalties by type, and handles appeals from reported users.',
      pending: 'Pending reports',
      penalized: 'Penalized',
      pendingAppeals: 'Pending appeals',
      bannedUsers: 'Banned users',
      total: 'Total reports',
      filterStatus: 'Status',
      filterCategory: 'Category',
      allCategories: 'All categories',
      searchLabel: 'Search (username / description / ID)',
      searchPh: 'Press Enter to search',
      appealsQueue: 'Appeals queue',
      emptyFiltered: 'No reports under the current filter.',
      colCategory: 'Category',
      colTarget: 'Reported',
      colReporter: 'Reporter',
      colCredit: 'Credit',
      colTime: 'Time',
      banned: 'Banned',
      handle: 'Handle',
      appeal: 'Appeal!',
      // detail
      detailTitle: 'Handle report',
      reportId: 'Report ID',
      reportDetail: 'Report details',
      suggestedDeduct: 'suggested −{n}',
      reporter: 'Reporter',
      target: 'Reported user',
      bannedUntil: 'Banned until {date}',
      targetCredit: 'Reported user credit',
      relatedEvent: 'Related event',
      reportNote: 'Report note',
      submittedAt: 'Submitted at',
      resolution: 'Resolution',
      violationHistory: 'Violation history',
      noViolations: 'No violation records.',
      appealRecords: 'Appeal records',
      noReason: '(no reason provided)',
      decisionNote: 'Decision note:',
      noAppeals: 'No appeals for this report.',
      accept: 'Accept (revoke penalty)',
      reject: 'Reject',
      decision: 'Decision',
      outcome: 'Outcome',
      banDuration: 'Ban duration',
      noteToUser: 'Note / message to user',
      notePh: 'Optional, sent to the relevant users with the notification',
      execute: 'Execute',
      confirmMax: 'Confirm the maximum penalty (ban) on the reported user?',
      confirmResolve: 'Confirm this decision?',
      resolveDone: 'Done, notification sent.',
      resolveFailed: 'Failed to resolve',
      currentlyBanned: 'This user is currently banned.',
      liftBan: 'Lift ban',
      banLifted: 'Ban lifted.',
      liftBanFailed: 'Failed to lift ban',
      penaltyRule: 'Penalty rules: harassment −10, dangerous burial −15, false info/activity −8, fee abuse/spam −5, no-show −3, other −3; max penalty doubles and bans.',
      viewAllAppeals: 'View all appeals',
      promptRestoreAccept: 'Accept appeal: restore credit (0-100, blank = 0)',
      promptNote: 'Decision note (optional)',
      promptRejectReason: 'Reject reason (optional)',
      promptRestoreLift: 'Lift ban: restore credit (0-100, blank = 0)',
      appealAccepted: 'Appeal accepted, penalty revoked.',
      appealRejected: 'Appeal rejected.',
      appealFailed: 'Failed to handle appeal',
      notFound: 'Report not found.',
    },
    appeals: {
      title: 'Appeals queue',
      subtitle: 'Appeals submitted by reported users against decisions. Accepting revokes the original penalty and can restore credit.',
      backToReports: '← Reports',
      emptyFiltered: 'No appeals under the current filter.',
      colAppellant: 'Appellant',
      colOrigCategory: 'Original category',
      colReason: 'Reason',
      colStatus: 'Status',
      colTime: 'Time',
      noReason: '(not provided)',
      accept: 'Accept',
      reject: 'Reject',
      statusPending: 'Pending',
    },
    rewardStore: {
      title: 'Explorer Points Store',
      subtitle: 'Manage rewards players can redeem with explorer points, and process redemption requests. Players earn explorer points by completing urban exploration routes.',
      itemsHeading: 'Items ({n})',
      newItem: 'New item',
      fName: 'Name *',
      fNamePh: 'e.g. omw custom sticker pack',
      fCost: 'Points required *',
      fStock: 'Stock (blank = unlimited)',
      fSort: 'Sort (smaller first)',
      fDesc: 'Description',
      fDescPh: 'Redemption details, how to claim, etc.',
      fImage: 'Image URL (optional)',
      saveEdit: 'Save changes',
      emptyItems: 'No items yet. Click “New item” to create one.',
      colName: 'Name',
      colCost: 'Points',
      colStock: 'Stock',
      colRedeemed: 'Redeemed',
      onShelf: 'Active',
      offShelf: 'Unpublished',
      putOn: 'Publish',
      putOff: 'Unpublish',
      itemUpdated: 'Item updated.',
      itemCreated: 'Item created.',
      putOnDone: 'Published.',
      putOffDone: 'Unpublished.',
      needTitle: 'Please enter the item name',
      needCost: 'Points required must be a positive integer',
      requestsHeading: 'Redemption requests',
      emptyRequests: 'No redemption requests under the current filter.',
      colUser: 'User',
      colItem: 'Item',
      colSpent: 'Points spent',
      colRequestedAt: 'Requested at',
      colNote: 'Note',
      fulfill: 'Fulfill',
      cancelRefund: 'Cancel & refund',
      fulfilled: 'Marked as fulfilled.',
      cancelledRefunded: 'Cancelled, points refunded to user.',
      promptFulfill: 'Fulfillment note (optional, e.g. code / shipping info)',
      promptCancel: 'Cancellation reason (optional, points will be refunded)',
    },
    voucherAuth: {
      title: 'Voucher redemption',
      subtitle: 'Authenticate route-completion reward vouchers submitted to your store, and redeem them by entering the one-time code when the player arrives.',
      inStore: 'In-store redemption',
      codeLabel: 'Redemption code (RV-XXXX-XXXX)',
      redeem: 'Redeem',
      redeeming: 'Redeeming…',
      redeemOk: 'Redeemed: {title}',
      voucherFallback: 'Voucher',
      tabPending: 'Pending',
      tabAuthenticated: 'Authenticated',
      tabAll: 'All',
      empty: 'No vouchers match.',
      colVoucher: 'Voucher',
      colRoute: 'Route',
      colCreator: 'Creator',
      colValue: 'Value',
      colQuota: 'Quota/claimed/redeemed',
      colAction: 'Action',
      approve: 'Approve',
      reject: 'Reject',
      rejectPrompt: 'Reject reason (optional):',
      freeGift: 'Free gift',
      statusAuthenticated: 'Authenticated',
      statusRejected: 'Rejected',
      statusPending: 'Pending',
      errNotFound: 'Redemption code not found',
      errNotAuthorized: 'This voucher does not belong to your store',
      errNotAuthenticated: 'This voucher is not authenticated yet',
      errAlreadyRedeemed: 'This voucher was already redeemed',
      errExpired: 'This voucher has expired',
      redeemFailed: 'Redeem failed',
    },
    routeRewards: {
      title: 'Route exploration rewards',
      subtitle: 'Set completion reward vouchers (for the first N finishers) on urban exploration routes you created. Pick a route to start editing.',
      empty: 'You haven’t created any routes yet. Create an urban exploration route in the app, then come back here to set rewards.',
      colRoute: 'Route',
      colCity: 'City',
      colCompletionReward: 'Completion reward',
      colAction: 'Action',
      enabled: 'Enabled',
      notSet: 'Not set',
      editReward: 'Edit reward',
      addReward: 'Add reward',
      editorTitle: 'Edit completion reward',
      editorSubtitle: 'Route “{title}” — set vouchers awarded to the “first N finishers”. Vouchers tied to a store require merchant authentication before players can redeem.',
      loadRewardFailed: 'Failed to load rewards',
      voucherN: 'Voucher #{n}',
      fTitle: 'Title',
      fTitlePh: 'e.g. one free pour-over coffee',
      fDescOptional: 'Description (optional)',
      fType: 'Type',
      fDiscountPercent: 'Discount %',
      fValueDollar: 'Value ($)',
      fQuota: 'Quota (first N)',
      fStore: 'Designated store (optional — otherwise self-serve voucher)',
      searchStorePh: 'Search store name / username',
      selfServe: 'Self-serve voucher',
      lockedNote: 'Issued vouchers cannot be modified; you can only add or delete unclaimed vouchers.',
      addVoucher: '+ Add voucher',
      savedOk: 'Saved. Authentication requests were sent for new vouchers tied to a designated store.',
      certified: 'Certified',
    },
  },
  zh: {
    lang: { en: 'EN', zh: '中' },
    common: {
      loading: '加载中…',
      save: '保存',
      saving: '保存中…',
      saveDraft: '保存草稿',
      cancel: '取消',
      delete: '删除',
      remove: '移除',
      edit: '编辑',
      detail: '详情',
      refresh: '刷新',
      back: '返回',
      backToList: '← 返回列表',
      search: '搜索',
      query: '查询',
      status: '状态',
      all: '全部',
      logout: '退出',
      unlimited: '不限',
      none: '无',
      optional: '可选',
      processing: '处理中…',
      dash: '—',
      create: '创建',
      creating: '创建中…',
      loadFailed: '加载失败',
      saveFailed: '保存失败',
      createFailed: '创建失败',
      deleteFailed: '删除失败',
      updateFailed: '更新失败',
      actionFailed: '操作失败',
    },
    adminNav: {
      brand: 'OnMyWay 管理后台',
      pvp: '限时活动',
      activities: '活动奖励',
      newActivity: '新建活动',
      reports: '举报处理',
      appeals: '申诉队列',
      rewards: '积分兑换',
    },
    orgNav: {
      brand: 'OnMyWay · 活动组织者',
      myActivities: '我的活动',
      create: '创建活动',
      routeRewards: '路线奖励',
      vouchers: '奖励核销',
    },
    adminLogin: {
      title: 'OnMyWay 管理后台',
      subtitle: '使用管理员账户登录以进入运营管理后台。',
      userLabel: '用户名 / 邮箱',
      passwordLabel: '密码',
      signIn: '登录',
      signingIn: '登录中…',
      notAdmin: '此账户不是管理员账户。活动组织者 / 商户请前往活动管理后台。',
      goOrganizer: '前往活动管理后台',
      organizerPrompt: '活动组织者 / 商户？',
      organizerLink: '前往活动管理后台',
      failed: '登录失败',
    },
    orgLogin: {
      title: '活动组织者',
      titleFull: '活动组织者登录',
      loadingTitle: '活动组织者',
      subtitle: '使用你的 OnMyWay App 账号登录，创建与管理活动、积分、徽章、抽奖与奖励兑换。',
      userLabel: '用户名 / 邮箱 / 手机号',
      passwordLabel: '密码',
      signIn: '登录',
      signingIn: '登录中…',
      adminPrompt: 'OnMyWay 运营管理员？',
      adminLink: '前往运营管理后台',
      failed: '登录失败',
    },
    activities: {
      listTitle: '我的活动',
      listTitleAdmin: '活动奖励',
      listSubtitle: '创建活动奖励配置：填写模板或用一句话描述，AI 编译成可执行规则，确认后上架。',
      createTemplate: '+ 用模板创建',
      createNL: '用一句话创建',
      empty: '还没有活动，点击上方按钮开始创建。',
      colName: '活动名称',
      colStatus: '状态',
      colType: '类型',
      colStart: '开始',
      colEnd: '结束',
      colUpdated: '更新时间',
      unnamed: '(未命名)',
      typeNL: '自然语言',
      typeTemplate: '模板',
      editorUnnamed: '(未命名活动)',
      notFound: '未找到活动',
      subtitleNL: '用一句话描述活动，AI 编译成可执行规则。',
      subtitleTpl: '按 7 个模块填写，AI 会编译成后端可执行的活动规则。请勿修改字段结构。',
      draftSaved: '已保存草稿',
      draftRestored: '已从本设备恢复未保存的修改。',
      draftDiscard: '放弃并载入已保存版本',
      compileOk: 'AI 已生成活动规则，请在下方确认',
      published: '已上架！',
      createdNeedRouteSetup: '已创建！部分路线包含需实物埋藏的节点——请在 app 的草稿箱中打开，到现场完成埋藏后这些路线即可上架。',
      goRouteSetup: '发送到 app 完成路线设置',
      routesPublished: '已创建并上架！所有路线均已在网页端完成。',
      // 路线协作台（AI 协作台）
      secRouteWorkbench: '01a · 路线创作',
      routeWorkbenchHint: '你可以自行填写每个节点，或（会员）用 AI 生成初稿。虚拟节点（仅需 GPS）可完全在网页端完成放置；需要埋藏实物的节点则在 app 内到现场完成。',
      rwManualNote: 'AI 初稿为会员专属；非会员也可在下方手动填写每个节点。',
      rwNodes: '个节点',
      rwNode: '节点',
      rwRouteTitle: '路线标题',
      rwRouteSynopsis: '路线简介',
      rwTheme: '主题',
      rwThemePh: '例如：追光记者的都市探案、图书馆建筑、公共艺术',
      rwStyle: '风格选择',
      rwDeadline: '截止日期（短期限定）',
      rwDeadlineHint: '到期后该路线在 app 内将无法再进入 play，玩家只能查看详情（评论、数据统计）。',
      rwInspiration: '告诉我你的灵感点',
      rwInspirationPh: '描述你想探索的氛围、建筑细节、希望串联的故事或任何灵感',
      rwNodeTypes: '节点类型偏好',
      rwNodeTypesHint: '选择你希望本路线包含的谜题类型，AI 会据此设计节点（至少选 1 种）。',
      rwAiGenerate: 'AI 生成初稿',
      rwAiGenerating: '生成中…',
      rwNodeNoGps: '缺少 GPS — 请在上方地图上定位',
      rwNodePhysical: '需埋藏实物',
      rwNodeQuestion: '谜题 / 问题',
      rwNodeAnswer: '答案',
      rwNodeHint: '提示',
      rwNodePhysicalHint: '该节点需要在现场埋藏实物 — 请在 app 内完成。',
      rwNodeVirtualHint: '虚拟节点 — 有 GPS 与答案后即在网页端完成放置。',
      rwNodeMedia: '节点图片 / 视频',
      rwNodeMediaHint: ' — 玩家到达该关卡时作为转场背景显示',
      rwNodeMediaAdd: '上传媒体',
      rwAiRefine: '与 AI 对话改进',
      rwAiRefinePh: '如「把节点2改难一点」或「调整主题」',
      rwAiRefineSend: '发送',
      rwRouteNeedsApp: '含实物节点 → 点击「创建」后在 app 内完成',
      rwRouteWebComplete: '全为虚拟节点 → 点击「创建」时在网页端完成',
      rwVirtualPlaced: '虚拟已放置',
      rwPhysicalCount: '实物节点',
      aiDraftDone: 'AI 初稿已应用到该路线。',
      aiDraftFailed: 'AI 生成失败。',
      aiPremiumOnly: 'AI 初稿为会员专属功能——你仍可在下方手动填写每个节点。',
      routeNeedsWaypoints: '请先在地图上绘制至少一个节点。',
      archived: '已下架',
      hasErrors: '规则存在错误，请先修正',
      publishFailed: '上架失败',
      archiveFailed: '下架失败',
      compileFailed: '编译失败',
      simulateFailed: '模拟失败',
      confirmDelete: '确认删除这个活动配置？',
      drawDone: '已开奖，{n} 位中奖',
      drawFailed: '开奖失败',
      aiRules: '🤖 AI 生成的活动规则',
      pointsGroup: 'Points 积分',
      progressionGroup: 'Progression 进阶',
      rewardsGroup: 'Rewards 奖励',
      lotteryGroup: 'Lottery 抽奖',
      simulate: 'Simulate 模拟',
      routesDone: '完成路线数',
      includeJoin: '含加入活动',
      runSim: '运行模拟',
      simPoints: '积分',
      simRoutesCompleted: '完成路线',
      simTickets: '抽奖券',
      simBadges: '解锁徽章',
      simRedeemable: '可兑换',
      readyToPublish: '✅ 准备发布',
      simSec: '🧪 奖励与积分模拟器',
      simSecHint: '按预计参与人数，为每个 Action 填写「预计有多少人会完成并获得积分」。数据实时更新，帮助你平衡积分发放、奖励成本、奖品与赞助预算。',
      simTargetLabel: '预计参与人数',
      simNoRules: '请先在上方添加积分规则，再进行奖励经济模拟。',
      simActionCol: 'Action',
      simPointsEachCol: '每次积分',
      simExpectedCol: '完成人数',
      simSubtotalCol: '发放积分',
      simTotalMinted: '预计发放总积分',
      simAvgPerParticipant: '人均积分',
      simEconomy: '经济概览',
      simTotalPoints: '预计发放总积分',
      simSetTarget: '填写预计参与人数后可估算人均积分。',
      simRewardCost: '奖励所需总积分：',
      simBudget: '赞助预算：',
      simAffordable: '人均积分可兑换：{list}',
      simNoneAffordable: '按当前人均积分暂时无法兑换任何奖励——可考虑降低兑换积分或提高积分奖励。',
      qrSec: '📱 参与二维码',
      qrSecHint: '将这些二维码打印并在活动现场展示。参与者用 App 内扫一扫或 iOS 相机扫描，即可参与活动或参与限时小活动并获得积分。',
      qrPublishFirst: '二维码需在活动发布后方可使用。',
      qrJoinTitle: '参与活动',
      qrDownload: '下载 PNG',
      optimizeTitle: '🤖 AI 积分规则助手',
      optimizeHint: '与 AI 经济顾问对话。它会读取你的赞助商、奖励、预计参与人数与抽奖设置，给出更均衡的积分规则方案，一键应用。',
      optimizeThinking: '思考中…',
      optimizeSuggested: '建议的积分规则',
      optimizeApply: '应用这些规则',
      optimizeApplied: '已应用 AI 建议的积分规则。',
      optimizeSend: '发送',
      optimizePlaceholder: '例如：预计 500 人，把发放总量控制在预算内，同时让大奖看起来可达成…',
      optimizeAutoAsk: '请审阅我当前的积分规则并给出优化后的方案。',
      optimizeFailed: '优化失败，请重试。',
      confirmPublish: '✓ 确认并上架',
      editRules: '编辑规则',
      publishedSuccess: '✅ 活动已创建并上架 App',
      editPublished: '编辑活动',
      updatePublished: '更新已上架活动',
      updatePublishedDone: '活动已更新，修改内容已同步至 App。',
      updatePublishedFailed: '更新已上架活动失败。',
      unpublish: '下架',
      ops: '运营数据',
      opsHint: '实时查看参与热度、路线表现、限时小活动签到、奖励经济与近 14 天趋势。',
      registrations: '报名人数',
      participants: '参与人数',
      active7d: '近 7 天活跃',
      engagementRate: '积分任务参与率',
      pointsAwarded: '累计发放积分',
      routeCompletions: '路线通关次数',
      routeCompletionRate: '路线通关率',
      totalDistanceEstimated: '总行程里程（估算）',
      avgDistanceEstimated: '路线参与者人均里程（估算）',
      avgRouteDuration: '平均路线参与时长',
      redemptions: '奖励兑换次数',
      tickets: '抽奖券',
      winners: '中奖人数',
      distanceMethodHint: '里程按「路线配置长度 × 通关次数」估算；路线时长使用参与者开始至通关的实测时间。限时小活动尚未采集 GPS 与离场时间，因此参与时长按活动排期估算。',
      routeAnalytics: '路线探索表现',
      sessionAnalytics: '限时小活动表现',
      actionAnalytics: '积分 Action 参与情况',
      heatTrend: '近 14 天新增参与者',
      analyticsName: '名称',
      analyticsParticipants: '参与人数',
      analyticsCompletions: '通关次数',
      analyticsCompletionRate: '通关率',
      analyticsAvgDuration: '平均参与时长',
      analyticsRouteKm: '路线里程 km',
      analyticsTotalKm: '总行程 km（估算）',
      analyticsSchedule: '活动时间',
      analyticsCheckins: '签到次数',
      analyticsScheduledDuration: '排期时长',
      analyticsParticipationTime: '总参与时长（估算）',
      analyticsNoData: '暂无参与数据。',
      people: '人',
      times: '次',
      draw: '开奖',
      nlHeading: '用一句话描述你的活动',
      nlHint: '例：“帮我创建一个两周的 Sydney hiking challenge，完成 5 条路线送一个 Patagonia cap，每 500 积分可以抽一次奖，最多抽 5 次。”',
      activityDesc: '活动描述',
      genRules: '创建',
      sec01: '01 · 活动信息',
      fName: '活动名称*',
      fType: '活动类型',
      geoLabel: '地点 / 路线',
      geoHint: ' · 添加一个或多个地点，或在地图上连点成一条 / 多条路线（可组合）',
      fDesc: '活动描述*',
      fStart: '开始日期*',
      fEnd: '结束日期*',
      fRedeemDeadline: '兑换截止日期',
      fTarget: '目标人数',
      mediaLabel: '活动图片 / 视频',
      mediaHint: ' · 将显示在 App 活动详情轮播中（第一张为封面，可拖拽排序）',
      uploadMedia: '添加图片 / 视频',
      mediaCover: '封面',
      mediaImage: '图片',
      mediaVideo: '视频',
      uploadMediaBadType: '不支持的文件类型（请使用 PNG / JPG / WebP / MP4 / MOV）',
      publishNeedLocation: '发布前请先在地图上添加至少一个地点或路线',
      geoPoint: '地点',
      geoRoute: '路线',
      // 01b · 限时小活动
      secSessions: '01b · 限时小活动',
      secSessionsHint: '可选。把大活动拆分成不同日期的限时小活动（如第一天晨间徒步、第二天工作坊）。每个小活动可绑定上方绘制的某个地点/路线。',
      fSessionName: '小活动名称*',
      fSessionDate: '日期',
      fSessionStart: '开始时间',
      fSessionEnd: '结束时间',
      fSessionLocation: '地点（可选）',
      fSessionRoute: '路线（可选）',
      fSessionDesc: '备注',
      sessionNoPlace: '— 不绑定地点 —',
      sessionNoRoute: '— 不绑定路线 —',
      addSession: '+ 添加限时小活动',
      removeSession: '移除小活动',
      sec02: '02 · 赞助商 & 商户',
      sec02Hint: '输入商户名称即可搜索并自动关联其 OnMyWay 账号。在 06/07 奖励区把奖励指派给某赞助商后，会自动显示在该赞助商下方。',
      fMerchantName: '商户名称',
      fMerchantUserId: 'OnMyWay User ID',
      fSponsorType: '类型',
      fContribution: '贡献',
      fBudget: '预算 (AUD)',
      fRewardRefId: 'Reward/Coupon ID',
      removeSponsor: '移除赞助商',
      addSponsor: '+ 添加赞助商',
      merchantSearchPh: '输入以搜索商户…',
      merchantLinked: '已关联',
      merchantUnlink: '取消关联',
      merchantUnlinked: '未关联 —— 请从列表中选择商户以关联账号。',
      merchantNoResults: '未找到匹配的商户',
      sponsorRewardsTitle: '该赞助商提供的奖励',
      sponsorRewardsEmpty: '尚未指派奖励。请在 06/07 奖励区创建奖励并选择此赞助商。',
      sponsorRewardsNeedLink: '请先关联商户账号，才能指派奖励。',
      untitledReward: '（未命名奖励）',
      rewardIssued: '已发行',
      rewardPendingIssue: '待发行',
      fRewardSponsor: '赞助商',
      rewardSponsorNone: '未指派',
      // Sponsor issuance confirmation
      confirmIssue: '确认发行',
      issueResend: '重新发送请求',
      issueSent: '发行请求已发送，赞助商将在 App 中确认。',
      issueResent: '已向赞助商重新发送请求。',
      issuePending: '等待赞助商确认',
      issueConfirmed: '已确认',
      issueRejected: '已拒绝',
      issueNotSent: '未发起',
      issueNoSponsor: '请先为该奖励指派已关联的赞助商。',
      issueRewardNotFound: '请先保存奖励再发起发行。',
      issueRefresh: '刷新',
      sec03: '03 · 积分获取规则',
      sec03Hint: '每个 Action 对应一种玩家积分获取途径（扫码加入、完成路线、邀请好友…）。系统会根据 Action 自动执行对应的完成验证。',
      fAction: 'Action',
      fPoints: '积分',
      fDailyLimit: '每日上限',
      fTotalLimit: '总上限',
      fVerification: '验证方式',
      verificationFixedHint: '由 Action 自动决定：完成路线＝路线探索通关（GPS）；参与活动 / 参与限时小活动＝扫描二维码；邀请好友 / 提交评价＝系统。',
      fSessionBind: '关联限时小活动',
      sessionBindAny: '任意限时小活动',
      fRuleDesc: '说明',
      unlimitedPh: '不限',
      addPointRule: '+ 添加积分规则',
      sec04: '04 · 路线 / 徽章进阶',
      fBadgeName: '徽章名称',
      fBadgeEmoji: '图标 Emoji',
      fBadgeImage: '徽章图片 / SVG',
      uploadImage: '上传图片',
      uploading: '上传中…',
      removeImage: '移除图片',
      uploadFailed: '上传失败',
      uploadTooLarge: '文件过大',
      uploadBadType: '不支持的文件类型（请使用 PNG / JPG / WebP / SVG）',
      storageOff: '服务器未启用上传功能',
      fBadgeType: '类型',
      fLevel: '等级',
      fRequirement: '获取条件',
      fCountThreshold: '数量/阈值',
      fRouteId: '路线',
      fLocationId: '地点',
      selectRoute: '选择路线…',
      selectLocation: '选择地点…',
      fBadgePoints: '附带积分',
      removeBadge: '移除徽章',
      addBadge: '+ 添加徽章',
      sec05: '05 · 抽奖配置',
      enableLottery: '启用抽奖',
      fPointsPerTicket: '每 N 积分 = 1 券',
      fEntryThreshold: '或 入场积分门槛',
      fMaxEntries: '每人最多券数',
      fLotteryDate: '开奖日期',
      fWinnerCount: '奖品数量',
      fPrize: '奖品',
      fSponsorUserId: '赞助商 User ID',
      lotteryEntryHint: '玩家如何获得抽奖券（所有奖池共享同一批券）。',
      ticketTiersTitle: '积分兑换抽奖券档位',
      ticketTiersHint: '让玩家用积分按自定义档位兑换抽奖券（例如 100 积分兑 1 张、500 积分兑 6 张）。设置任意档位后，玩家将主动用积分兑换抽奖券，而不再自动累积；上方“每 N 积分 = 1 券”的设置将被忽略。',
      fTierPoints: '消耗积分',
      fTierTickets: '获得抽奖券',
      addTier: '+ 添加兑换档位',
      removeTier: '移除档位',
      ticketDropsTitle: '路线探索随机掉落抽奖券',
      ticketDropsHint: '在路线探索途中随机撒落抽奖券。玩家到达节点时有机会拾取。',
      enableTicketDrops: '启用随机掉落',
      fDropTotal: '掉落总数量',
      fDropMaxPerUser: '每人最多拾取',
      fDropRate: '每个节点掉落概率（0–1）',
      fDropStart: '掉落时段开始',
      fDropEnd: '掉落时段结束',
      lotteryPoolsTitle: '奖池',
      lotteryPoolsHint: '每份奖品添加一个奖池并填写数量。抽奖券共享，系统按券数加权抽取，并确保每份奖品都会被抽中送出。',
      fPoolName: '奖池名称',
      poolNamePh: '如：一等奖',
      addPool: '+ 添加奖池',
      removePool: '移除奖池',
      sec0607: '06 / 07 · 奖励兑换 & 库存',
      sec0607Hint: '三种兑换模式：仅积分 / 积分+徽章 / 仅徽章。设置库存与每人上限。',
      fRewardName: '奖励名称',
      fRedemptionMode: '兑换模式',
      fCostPoints: '所需积分',
      fRequiredBadge: '所需徽章',
      selectBadge: '选择徽章…',
      fRewardKind: '类型',
      fValue: '价值 (AUD)',
      fInventoryPolicy: '库存策略',
      fInventory: '库存数量',
      fPerUserLimit: '每人上限',
      fTotalBudget: '总预算 (AUD)',
      fExpiryType: '有效期类型',
      fDays: '天数',
      fDate: '日期',
      removeReward: '移除奖励',
      addReward: '+ 添加奖励',
    },
    campaigns: {
      dashTitle: '限时活动 Dashboard',
      dashSubtitle: '查看所有官方 PvP 限时活动、参与数据，并创建或编辑活动。',
      totalEvents: '活动总数',
      active: '进行中',
      totalParticipants: '总参与人数',
      totalMatches: '总对局数',
      newEvent: '+ 新建活动',
      emptyList: '暂无活动，点击「新建活动」创建第一个限时活动。',
      colEvent: '活动',
      colStatus: '状态',
      colParticipants: '参与人数',
      colMatches: '对局',
      colTime: '时间',
      createTitle: '新建限时活动',
      createSubtitle: '创建官方 PvP 藏找对战积分活动。slug 创建后不可修改。',
      createBtn: '创建活动',
      editTitle: '编辑限时活动',
      backToDetail: '返回详情',
      endEvent: '结束活动',
      confirmEndExport: '结束后不再累计积分，可导出排行榜用于线下发奖。确定结束？',
      confirmEnd: '结束后不再累计积分。确定结束此活动？',
      endFailed: '结束失败',
      titleZh: '中文标题',
      titleEn: '英文标题',
      descZh: '中文说明',
      descEn: '英文说明',
      statusDraft: '草稿',
      statusActive: '进行中',
      statusEnded: '已结束',
      startsAt: '开始时间 (ISO)',
      endsAt: '结束时间 (ISO)',
      setOpenEnded: '设为长期开放 (2099)',
      formRequired: '请填写 slug 与中英文标题',
      exportCsv: '导出排行榜 CSV',
      info: '活动信息',
      start: '开始：',
      end: '结束：',
      lastMatch: '最近对局：',
      noZhDesc: '（无中文说明）',
      leaderboard: '排行榜',
      noPoints: '暂无积分数据',
      colUser: '用户',
      colPoints: '积分',
      colPlayed: '场次',
      colWon: '胜场',
      lobbyRooms: '大厅房间',
      noRooms: '当前无等待中的房间',
      colRoomId: '房间 ID',
      colRoomName: '名称',
      colMode: '模式',
      colMembers: '人数',
    },
    reports: {
      title: '举报处理 Dashboard',
      subtitle: '汇总所有用户提交的举报，按类型执行处罚，并处理被举报人的申诉。',
      pending: '待处理举报',
      penalized: '已处罚',
      pendingAppeals: '待处理申诉',
      bannedUsers: '当前封禁用户',
      total: '举报总数',
      filterStatus: '状态',
      filterCategory: '类别',
      allCategories: '全部类别',
      searchLabel: '搜索（用户名 / 说明 / ID）',
      searchPh: '回车搜索',
      appealsQueue: '申诉队列',
      emptyFiltered: '当前筛选条件下暂无举报。',
      colCategory: '类别',
      colTarget: '被举报人',
      colReporter: '举报人',
      colCredit: '信用分',
      colTime: '时间',
      banned: '已封禁',
      handle: '处理',
      appeal: '申诉!',
      detailTitle: '举报处理',
      reportId: '举报 ID',
      reportDetail: '举报详情',
      suggestedDeduct: '建议扣分 -{n}',
      reporter: '举报人',
      target: '被举报人',
      bannedUntil: '已封禁至 {date}',
      targetCredit: '被举报人信用分',
      relatedEvent: '关联活动',
      reportNote: '举报说明',
      submittedAt: '提交时间',
      resolution: '处理结果',
      violationHistory: '被举报人违规历史',
      noViolations: '暂无违规记录。',
      appealRecords: '申诉记录',
      noReason: '（未填写理由）',
      decisionNote: '处理备注：',
      noAppeals: '该举报暂无申诉。',
      accept: '采纳（撤销处罚）',
      reject: '驳回',
      decision: '处理决定',
      outcome: '处理结果',
      banDuration: '封号时长',
      noteToUser: '备注 / 给用户的说明',
      notePh: '可选，会随通知发送给相关用户',
      execute: '执行处理',
      confirmMax: '确认对被举报人执行顶格处理（封号）？',
      confirmResolve: '确认执行该处理决定？',
      resolveDone: '处理完成，通知已下发。',
      resolveFailed: '处理失败',
      currentlyBanned: '该用户当前处于封禁状态。',
      liftBan: '解除封禁',
      banLifted: '封禁已解除。',
      liftBanFailed: '解除封禁失败',
      penaltyRule: '处罚扣分规则：骚扰 -10，危险埋藏 -15，虚假信息/活动 -8，费用滥用/垃圾 -5，爽约 -3，其他 -3；顶格处理翻倍并封号。',
      viewAllAppeals: '查看全部申诉',
      promptRestoreAccept: '采纳申诉：返还信用分（0-100，可留空为 0）',
      promptNote: '处理备注（可选）',
      promptRejectReason: '驳回理由（可选）',
      promptRestoreLift: '解除封禁：返还信用分（0-100，可留空为 0）',
      appealAccepted: '申诉已采纳，处罚已撤销。',
      appealRejected: '申诉已驳回。',
      appealFailed: '申诉处理失败',
      notFound: '未找到该举报。',
    },
    appeals: {
      title: '申诉队列',
      subtitle: '被举报人对处理结果提交的申诉。采纳后将撤销原处罚并可返还信用分。',
      backToReports: '← 举报列表',
      emptyFiltered: '当前筛选条件下暂无申诉。',
      colAppellant: '申诉人',
      colOrigCategory: '原举报类别',
      colReason: '申诉理由',
      colStatus: '状态',
      colTime: '时间',
      noReason: '（未填写）',
      accept: '采纳',
      reject: '驳回',
      statusPending: '待审核',
    },
    rewardStore: {
      title: '探索者积分兑换',
      subtitle: '管理玩家可用探索者积分兑换的奖励项，并处理兑换申请。玩家完成都市探索路线后获得探索者积分。',
      itemsHeading: '兑换项（{n}）',
      newItem: '新建兑换项',
      fName: '名称 *',
      fNamePh: '如：omw 定制贴纸包',
      fCost: '所需积分 *',
      fStock: '库存（留空 = 不限）',
      fSort: '排序（小的在前）',
      fDesc: '描述',
      fDescPh: '兑换说明、领取方式等',
      fImage: '图片 URL（可选）',
      saveEdit: '保存修改',
      emptyItems: '暂无兑换项，点击「新建兑换项」创建。',
      colName: '名称',
      colCost: '所需积分',
      colStock: '库存',
      colRedeemed: '已兑换',
      onShelf: '上架中',
      offShelf: '已下架',
      putOn: '上架',
      putOff: '下架',
      itemUpdated: '兑换项已更新。',
      itemCreated: '兑换项已创建。',
      putOnDone: '已上架。',
      putOffDone: '已下架。',
      needTitle: '请填写兑换项名称',
      needCost: '所需积分必须为正整数',
      requestsHeading: '兑换申请',
      emptyRequests: '当前筛选条件下暂无兑换申请。',
      colUser: '用户',
      colItem: '兑换项',
      colSpent: '消耗积分',
      colRequestedAt: '申请时间',
      colNote: '备注',
      fulfill: '发放',
      cancelRefund: '取消并退分',
      fulfilled: '已标记为已发放。',
      cancelledRefunded: '已取消，积分已退回用户。',
      promptFulfill: '发放备注（可选，如兑换码/发货信息）',
      promptCancel: '取消原因（可选，积分将退回）',
    },
    voucherAuth: {
      title: '奖励券核销',
      subtitle: '认证创作者提交给你店铺的路线完成奖励券，并在玩家到店时输入一次性核销码完成核销。',
      inStore: '到店核销',
      codeLabel: '核销码（RV-XXXX-XXXX）',
      redeem: '核销',
      redeeming: '核销中…',
      redeemOk: '核销成功：{title}',
      voucherFallback: '奖励券',
      tabPending: '待认证',
      tabAuthenticated: '已认证',
      tabAll: '全部',
      empty: '没有符合条件的奖励券。',
      colVoucher: '奖励券',
      colRoute: '路线',
      colCreator: '创作者',
      colValue: '面额',
      colQuota: '名额/已领/已核销',
      colAction: '操作',
      approve: '通过',
      reject: '拒绝',
      rejectPrompt: '拒绝理由（可选）：',
      freeGift: '免费赠品',
      statusAuthenticated: '已认证',
      statusRejected: '已拒绝',
      statusPending: '待认证',
      errNotFound: '找不到该核销码',
      errNotAuthorized: '该券不属于你的店铺',
      errNotAuthenticated: '该券尚未通过认证',
      errAlreadyRedeemed: '该券已被核销',
      errExpired: '该券已过期',
      redeemFailed: '核销失败',
    },
    routeRewards: {
      title: '路线探索奖励',
      subtitle: '为你创建的都市探索路线设置完成奖励券（前 N 位完成者可获得）。选择一条路线开始编辑。',
      empty: '你还没有创建路线。请在 App 中创建都市探索路线后回到这里设置奖励。',
      colRoute: '路线',
      colCity: '城市',
      colCompletionReward: '完成奖励',
      colAction: '操作',
      enabled: '已启用',
      notSet: '未设置',
      editReward: '编辑奖励',
      addReward: '添加奖励',
      editorTitle: '编辑完成奖励',
      editorSubtitle: '路线「{title}」——设置“前 N 位完成者可获得”的奖励券。指定店铺的券需商家认证后玩家方可核销。',
      loadRewardFailed: '加载奖励失败',
      voucherN: '奖励券 #{n}',
      fTitle: '标题',
      fTitlePh: '例如：免费手冲咖啡一杯',
      fDescOptional: '描述（可选）',
      fType: '类型',
      fDiscountPercent: '折扣百分比',
      fValueDollar: '面额（$）',
      fQuota: '名额（前 N 位）',
      fStore: '指定店铺（可选——不指定则为自助凭证）',
      searchStorePh: '搜索商家名称 / 用户名',
      selfServe: '自助凭证',
      lockedNote: '已发放的奖励券内容不可修改，只能新增或删除未被领取的券。',
      addVoucher: '+ 添加奖励券',
      savedOk: '已保存。指定店铺的新增奖励券已发送认证请求。',
      certified: '认证',
    },
  },
};

// Enum value → { en, zh } display maps, indexed by group.
export const portalEnums = {
  configStatus: {
    draft: { en: 'Draft', zh: '草稿' },
    compiled: { en: 'Ready to publish', zh: '待发布' },
    published: { en: 'Published', zh: '已上架' },
    archived: { en: 'Unpublished', zh: '已下架' },
  },
  activityType: {
    natural_language: { en: 'Natural language', zh: '自然语言' },
    template: { en: 'Template', zh: '模板' },
  },
  gatheringTag: {
    outdoor: { en: 'Outdoor Explore', zh: '户外探索' },
    sports: { en: 'Sports & Active', zh: '运动活动' },
    social: { en: 'Social Gathering', zh: '社交聚会' },
    culture: { en: 'Arts & Culture', zh: '艺术文化' },
    pets: { en: 'Pets & Pals', zh: '宠物同行' },
    wellness: { en: 'Wellness & Chill', zh: '放空疗愈' },
    learning: { en: 'Learn & Share', zh: '学习交流' },
    other: { en: 'Other', zh: '其他' },
  },
  action: {
    join_activity: { en: 'Join Activity', zh: '参与活动' },
    session_checkin: { en: 'Timed Mini-Activity', zh: '参与限时小活动' },
    route_start: { en: 'Route Start', zh: '开始路线' },
    route_complete: { en: 'Route Complete', zh: '完成路线' },
    badge_found: { en: 'Badge Found', zh: '找到徽章' },
    photo_upload: { en: 'Photo Upload', zh: '上传照片' },
    invite_friend: { en: 'Invite Friend', zh: '邀请好友' },
    friend_completes: { en: 'Friend Completes', zh: '好友完成' },
    merchant_checkin: { en: 'Merchant Check-in', zh: '商户签到' },
    daily_checkin: { en: 'Daily Check-in', zh: '每日签到' },
    review_submit: { en: 'Review Submit', zh: '提交评价' },
    custom: { en: 'Custom', zh: '自定义' },
  },
  verification: {
    system: { en: 'System', zh: '系统' },
    gps: { en: 'GPS', zh: 'GPS' },
    ai: { en: 'AI', zh: 'AI' },
    qr: { en: 'QR', zh: '二维码' },
    manual: { en: 'Manual', zh: '人工' },
  },
  badgeType: {
    progression: { en: 'Progression', zh: '进阶' },
    specific: { en: 'Specific', zh: '专属' },
  },
  badgeReq: {
    routes_completed: { en: 'Routes completed', zh: '完成N条路线' },
    route_specific: { en: 'Specific route', zh: '完成指定路线' },
    hidden_location: { en: 'Hidden location', zh: '找到隐藏点' },
    sponsor_visit: { en: 'Sponsor visit', zh: '到访赞助商' },
    points_threshold: { en: 'Points threshold', zh: '达到积分' },
  },
  redemptionMode: {
    points_only: { en: 'Points only', zh: '仅积分' },
    points_and_badge: { en: 'Points + Badge', zh: '积分+徽章' },
    badge_only: { en: 'Badge only', zh: '仅徽章' },
  },
  inventoryPolicy: {
    limited: { en: 'Limited', zh: '限量' },
    first_come_first_served: { en: 'First come first served', zh: '先到先得' },
    unlimited: { en: 'Unlimited', zh: '无限' },
  },
  rewardKind: {
    coupon: { en: 'Coupon', zh: '优惠券' },
    physical: { en: 'Physical', zh: '实物' },
    experience: { en: 'Experience', zh: '体验' },
    digital: { en: 'Digital', zh: '数字' },
  },
  expiryType: {
    days: { en: 'Days after issue', zh: '领取后N天' },
    event_date: { en: 'Event/redemption deadline', zh: '活动截止' },
    fixed_date: { en: 'Fixed date', zh: '指定日期' },
  },
  sponsorType: {
    main: { en: 'Main Sponsor', zh: '主赞助' },
    supporting: { en: 'Supporting Sponsor', zh: '协办' },
  },
  couponType: {
    free_item: { en: 'Free item', zh: '免费赠品' },
    fixed_discount: { en: 'Fixed discount', zh: '固定金额抵扣' },
    percentage: { en: 'Percentage off', zh: '百分比折扣' },
    custom: { en: 'Custom', zh: '自定义' },
  },
  campaignStatus: {
    draft: { en: 'Draft', zh: '草稿' },
    active: { en: 'Active', zh: '进行中' },
    ended: { en: 'Ended', zh: '已结束' },
    rewards_distributed: { en: 'Rewards distributed', zh: '已发奖' },
  },
  redemptionStatus: {
    pending: { en: 'Pending', zh: '待发放' },
    fulfilled: { en: 'Fulfilled', zh: '已发放' },
    cancelled: { en: 'Cancelled', zh: '已取消' },
  },
  redemptionStatusFilter: {
    pending: { en: 'Pending', zh: '待发放' },
    all: { en: 'All', zh: '全部' },
    fulfilled: { en: 'Fulfilled', zh: '已发放' },
    cancelled: { en: 'Cancelled', zh: '已取消' },
  },
  reportCategory: {
    harassment: { en: 'Harassment/abuse', zh: '骚扰/辱骂' },
    dangerous_burial: { en: 'Dangerous burial', zh: '危险埋藏' },
    fake_info: { en: 'False information', zh: '虚假信息' },
    fake_event: { en: 'Fake event', zh: '虚假活动' },
    fee_abuse: { en: 'Fee abuse', zh: '费用滥用' },
    spam: { en: 'Spam', zh: '垃圾信息' },
    no_show: { en: 'No-show', zh: '爽约未到' },
    other: { en: 'Other', zh: '其他' },
  },
  reportStatus: {
    pending: { en: 'Pending', zh: '待处理' },
    confirmed: { en: 'Penalized', zh: '已处罚' },
    rejected: { en: 'Rejected', zh: '已驳回' },
    awaiting_evidence: { en: 'Awaiting evidence', zh: '待补充证据' },
    awaiting_response: { en: 'Awaiting response', zh: '待被举报人回应' },
  },
  reportStatusFilter: {
    pending: { en: 'Pending', zh: '待处理' },
    all: { en: 'All', zh: '全部' },
    confirmed: { en: 'Penalized', zh: '已处罚' },
    rejected: { en: 'Rejected', zh: '已驳回' },
    awaiting_evidence: { en: 'Awaiting evidence', zh: '待补充证据' },
    awaiting_response: { en: 'Awaiting response', zh: '待回应' },
  },
  reportOutcome: {
    success_normal: { en: 'Report upheld · normal penalty', zh: '举报成立 · 正常处罚' },
    success_max: { en: 'Report upheld · maximum penalty (ban)', zh: '举报成立 · 顶格处理（封号）' },
    reject_more_evidence: { en: 'Request more evidence from reporter', zh: '要求举报人补充证据' },
    reject_await_response: { en: 'Await reported user response', zh: '等待被举报人回应' },
    reject_failed: { en: 'Report not upheld · close', zh: '举报不成立 · 关闭' },
  },
  banDuration: {
    '1m': { en: 'Ban 1 month', zh: '封号 1 个月' },
    '3m': { en: 'Ban 3 months', zh: '封号 3 个月' },
    '6m': { en: 'Ban 6 months', zh: '封号 6 个月' },
    '1y': { en: 'Ban 1 year', zh: '封号 1 年' },
    permanent: { en: 'Permanent ban', zh: '永久封号' },
  },
  appealStatus: {
    pending: { en: 'Pending', zh: '待审核' },
    accepted: { en: 'Accepted', zh: '已采纳' },
    rejected: { en: 'Rejected', zh: '已驳回' },
  },
  appealStatusFilter: {
    pending: { en: 'Pending', zh: '待审核' },
    all: { en: 'All', zh: '全部' },
    accepted: { en: 'Accepted', zh: '已采纳' },
    rejected: { en: 'Rejected', zh: '已驳回' },
  },
};
